import { IPublicMenuTheme, LEGACY_PUBLIC_MENU_THEME, PublicMenuCardLayout } from "./PublicMenuTheme"

export interface ISystemPublicMenuTheme {
  id: string
  nameKey: string
  theme: IPublicMenuTheme
}

type ThemePalette = {
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  surfaceColor: string
  textColor: string
  buttonColor: string
  cardLayout?: PublicMenuCardLayout
  useGradientHeader?: boolean
}

const createPreset = (id: string, nameKey: string, palette: ThemePalette): ISystemPublicMenuTheme => {
  const theme: IPublicMenuTheme = {
    ...LEGACY_PUBLIC_MENU_THEME,
    ...palette,
    borderColor: palette.secondaryColor,
    searchBackgroundColor: palette.surfaceColor,
    searchBorderColor: palette.secondaryColor,
    headerBackgroundColor: palette.surfaceColor,
    restaurantNameColor: palette.textColor,
    restaurantDescriptionColor: palette.textColor,
    statusCardBackgroundColor: palette.secondaryColor,
    cartButtonColor: palette.primaryColor,
    cartBackgroundColor: palette.surfaceColor,
    cartBorderColor: palette.secondaryColor,
    cartTitleTextColor: palette.primaryColor,
    cartDescriptionTextColor: palette.textColor,
    cartTotalLabelTextColor: palette.textColor,
    cartTotalValueTextColor: palette.textColor,
    emptyStateTitleColor: palette.textColor,
    emptyStateDescriptionColor: palette.textColor,
    categoryBackgroundColor: palette.surfaceColor,
    categoryBorderColor: palette.secondaryColor,
    categoryDividerColor: palette.secondaryColor,
    loginButtonBackgroundColor: palette.surfaceColor,
    loginButtonTextColor: palette.textColor,
    shareButtonBackgroundColor: palette.secondaryColor,
    shareButtonTextColor: palette.primaryColor,
    serviceSelectorBackgroundColor: palette.secondaryColor,
    serviceSelectorTextColor: palette.textColor,
    serviceSelectorActiveBackgroundColor: palette.primaryColor,
    serviceSelectorActiveTextColor: palette.buttonColor === "#FFFFFF" ? palette.textColor : "#FFFFFF",
    mobileNavBackgroundColor: palette.surfaceColor,
    mobileNavActiveBackgroundColor: palette.secondaryColor,
    mobileNavActiveTextColor: palette.primaryColor,
    mobileNavActiveIconColor: palette.accentColor,
    mobileNavInactiveBackgroundColor: palette.surfaceColor,
    mobileNavInactiveTextColor: palette.textColor,
    mobileNavInactiveIconColor: palette.textColor,
  }

  return { id, nameKey, theme }
}

export const SYSTEM_PUBLIC_MENU_THEMES: ISystemPublicMenuTheme[] = [
  {
    id: "system-default",
    nameKey: "public_menu_customization.themes.preset_names.system_default",
    theme: LEGACY_PUBLIC_MENU_THEME,
  },
  createPreset("mothers-day", "public_menu_customization.themes.preset_names.mothers_day", {
    primaryColor: "#DB2777",
    secondaryColor: "#FCE7F3",
    accentColor: "#BE185D",
    backgroundColor: "#FFF7FB",
    surfaceColor: "#FFFFFF",
    textColor: "#4A1833",
    buttonColor: "#DB2777",
    useGradientHeader: true,
  }),
  createPreset("fathers-day", "public_menu_customization.themes.preset_names.fathers_day", {
    primaryColor: "#1D4ED8",
    secondaryColor: "#DBEAFE",
    accentColor: "#0F766E",
    backgroundColor: "#F8FAFC",
    surfaceColor: "#FFFFFF",
    textColor: "#172554",
    buttonColor: "#1D4ED8",
    cardLayout: "compact-left",
  }),
  createPreset("new-year", "public_menu_customization.themes.preset_names.new_year", {
    primaryColor: "#D4AF37",
    secondaryColor: "#27272A",
    accentColor: "#FDE68A",
    backgroundColor: "#09090B",
    surfaceColor: "#18181B",
    textColor: "#FAFAFA",
    buttonColor: "#D4AF37",
    useGradientHeader: true,
  }),
  createPreset("christmas", "public_menu_customization.themes.preset_names.christmas", {
    primaryColor: "#B91C1C",
    secondaryColor: "#DCFCE7",
    accentColor: "#15803D",
    backgroundColor: "#FFF7F7",
    surfaceColor: "#FFFFFF",
    textColor: "#3F1212",
    buttonColor: "#15803D",
    useGradientHeader: true,
  }),
  createPreset("valentines", "public_menu_customization.themes.preset_names.valentines", {
    primaryColor: "#E11D48",
    secondaryColor: "#FFE4E6",
    accentColor: "#9F1239",
    backgroundColor: "#FFF1F2",
    surfaceColor: "#FFFFFF",
    textColor: "#4C0519",
    buttonColor: "#E11D48",
  }),
  createPreset("easter", "public_menu_customization.themes.preset_names.easter", {
    primaryColor: "#7C3AED",
    secondaryColor: "#EDE9FE",
    accentColor: "#F59E0B",
    backgroundColor: "#FAF5FF",
    surfaceColor: "#FFFFFF",
    textColor: "#3B0764",
    buttonColor: "#7C3AED",
    cardLayout: "compact-right",
  }),
  createPreset("spring", "public_menu_customization.themes.preset_names.spring", {
    primaryColor: "#16A34A",
    secondaryColor: "#DCFCE7",
    accentColor: "#F472B6",
    backgroundColor: "#F0FDF4",
    surfaceColor: "#FFFFFF",
    textColor: "#14532D",
    buttonColor: "#16A34A",
    useGradientHeader: true,
  }),
  createPreset("summer", "public_menu_customization.themes.preset_names.summer", {
    primaryColor: "#F97316",
    secondaryColor: "#FFEDD5",
    accentColor: "#06B6D4",
    backgroundColor: "#FFF7ED",
    surfaceColor: "#FFFFFF",
    textColor: "#431407",
    buttonColor: "#06B6D4",
  }),
  createPreset("autumn", "public_menu_customization.themes.preset_names.autumn", {
    primaryColor: "#B45309",
    secondaryColor: "#FEF3C7",
    accentColor: "#92400E",
    backgroundColor: "#FFFBEB",
    surfaceColor: "#FFFFFF",
    textColor: "#451A03",
    buttonColor: "#B45309",
    cardLayout: "compact-left",
  }),
  createPreset("winter", "public_menu_customization.themes.preset_names.winter", {
    primaryColor: "#0369A1",
    secondaryColor: "#E0F2FE",
    accentColor: "#64748B",
    backgroundColor: "#F0F9FF",
    surfaceColor: "#FFFFFF",
    textColor: "#0C4A6E",
    buttonColor: "#0369A1",
  }),
  createPreset("night", "public_menu_customization.themes.preset_names.night", {
    primaryColor: "#A78BFA",
    secondaryColor: "#312E81",
    accentColor: "#FBBF24",
    backgroundColor: "#111827",
    surfaceColor: "#1F2937",
    textColor: "#F9FAFB",
    buttonColor: "#7C3AED",
    useGradientHeader: true,
  }),
  createPreset("coffee", "public_menu_customization.themes.preset_names.coffee", {
    primaryColor: "#92400E",
    secondaryColor: "#FEF3C7",
    accentColor: "#B45309",
    backgroundColor: "#FFFBEB",
    surfaceColor: "#FFFFFF",
    textColor: "#451A03",
    buttonColor: "#92400E",
    cardLayout: "compact-right",
  }),
  createPreset("burger", "public_menu_customization.themes.preset_names.burger", {
    primaryColor: "#F59E0B",
    secondaryColor: "#27272A",
    accentColor: "#EF4444",
    backgroundColor: "#18181B",
    surfaceColor: "#27272A",
    textColor: "#FAFAFA",
    buttonColor: "#EF4444",
    useGradientHeader: true,
  }),
  createPreset("pizza", "public_menu_customization.themes.preset_names.pizza", {
    primaryColor: "#DC2626",
    secondaryColor: "#FEE2E2",
    accentColor: "#16A34A",
    backgroundColor: "#FFF7ED",
    surfaceColor: "#FFFFFF",
    textColor: "#450A0A",
    buttonColor: "#16A34A",
  }),
  createPreset("sushi", "public_menu_customization.themes.preset_names.sushi", {
    primaryColor: "#BE123C",
    secondaryColor: "#1F2937",
    accentColor: "#F9A8D4",
    backgroundColor: "#111827",
    surfaceColor: "#1F2937",
    textColor: "#F9FAFB",
    buttonColor: "#BE123C",
    cardLayout: "compact-left",
  }),
  createPreset("healthy", "public_menu_customization.themes.preset_names.healthy", {
    primaryColor: "#65A30D",
    secondaryColor: "#ECFCCB",
    accentColor: "#16A34A",
    backgroundColor: "#F7FEE7",
    surfaceColor: "#FFFFFF",
    textColor: "#365314",
    buttonColor: "#65A30D",
  }),
  createPreset("ocean", "public_menu_customization.themes.preset_names.ocean", {
    primaryColor: "#0284C7",
    secondaryColor: "#E0F2FE",
    accentColor: "#0891B2",
    backgroundColor: "#F0F9FF",
    surfaceColor: "#FFFFFF",
    textColor: "#0C4A6E",
    buttonColor: "#0284C7",
    useGradientHeader: true,
  }),
  createPreset("lavender", "public_menu_customization.themes.preset_names.lavender", {
    primaryColor: "#8B5CF6",
    secondaryColor: "#EDE9FE",
    accentColor: "#C084FC",
    backgroundColor: "#FAF5FF",
    surfaceColor: "#FFFFFF",
    textColor: "#4C1D95",
    buttonColor: "#8B5CF6",
  }),
  createPreset("minimal", "public_menu_customization.themes.preset_names.minimal", {
    primaryColor: "#18181B",
    secondaryColor: "#F4F4F5",
    accentColor: "#71717A",
    backgroundColor: "#FFFFFF",
    surfaceColor: "#FFFFFF",
    textColor: "#18181B",
    buttonColor: "#18181B",
    cardLayout: "compact-left",
  }),
  createPreset("rose-gold", "public_menu_customization.themes.preset_names.rose_gold", {
    primaryColor: "#BE7C68",
    secondaryColor: "#FCE7E1",
    accentColor: "#9D5C4A",
    backgroundColor: "#FFF8F6",
    surfaceColor: "#FFFFFF",
    textColor: "#5C2E24",
    buttonColor: "#BE7C68",
    useGradientHeader: true,
  }),
  createPreset("neon", "public_menu_customization.themes.preset_names.neon", {
    primaryColor: "#22D3EE",
    secondaryColor: "#3F3F46",
    accentColor: "#A3E635",
    backgroundColor: "#09090B",
    surfaceColor: "#18181B",
    textColor: "#FAFAFA",
    buttonColor: "#22D3EE",
    useGradientHeader: true,
  }),
  createPreset("corporate", "public_menu_customization.themes.preset_names.corporate", {
    primaryColor: "#334155",
    secondaryColor: "#E2E8F0",
    accentColor: "#0F766E",
    backgroundColor: "#F8FAFC",
    surfaceColor: "#FFFFFF",
    textColor: "#0F172A",
    buttonColor: "#0F766E",
    cardLayout: "compact-right",
  }),
  createPreset("candy", "public_menu_customization.themes.preset_names.candy", {
    primaryColor: "#EC4899",
    secondaryColor: "#FCE7F3",
    accentColor: "#8B5CF6",
    backgroundColor: "#FDF4FF",
    surfaceColor: "#FFFFFF",
    textColor: "#581C87",
    buttonColor: "#EC4899",
    useGradientHeader: true,
  }),
  createPreset("forest", "public_menu_customization.themes.preset_names.forest", {
    primaryColor: "#166534",
    secondaryColor: "#DCFCE7",
    accentColor: "#854D0E",
    backgroundColor: "#F0FDF4",
    surfaceColor: "#FFFFFF",
    textColor: "#14532D",
    buttonColor: "#166534",
  }),
  createPreset("sunset", "public_menu_customization.themes.preset_names.sunset", {
    primaryColor: "#EA580C",
    secondaryColor: "#FFEDD5",
    accentColor: "#DB2777",
    backgroundColor: "#FFF7ED",
    surfaceColor: "#FFFFFF",
    textColor: "#7C2D12",
    buttonColor: "#DB2777",
    useGradientHeader: true,
  }),
  createPreset("black-gold", "public_menu_customization.themes.preset_names.black_gold", {
    primaryColor: "#EAB308",
    secondaryColor: "#27272A",
    accentColor: "#FDE047",
    backgroundColor: "#09090B",
    surfaceColor: "#18181B",
    textColor: "#FAFAFA",
    buttonColor: "#EAB308",
    cardLayout: "compact-right",
  }),
    createPreset("acai", "public_menu_customization.themes.preset_names.acai", {
        primaryColor: "#6D28D9",
        secondaryColor: "#EDE9FE",
        accentColor: "#A21CAF",
        backgroundColor: "#FAF5FF",
        surfaceColor: "#FFFFFF",
        textColor: "#3B0764",
        buttonColor: "#6D28D9",
        useGradientHeader: true,
    }),
    createPreset("churrasco", "public_menu_customization.themes.preset_names.churrasco", {
        primaryColor: "#991B1B",
        secondaryColor: "#FEE2E2",
        accentColor: "#F97316",
        backgroundColor: "#FFF7ED",
        surfaceColor: "#FFFFFF",
        textColor: "#450A0A",
        buttonColor: "#991B1B",
        cardLayout: "compact-left",
    }),
    createPreset("pastel", "public_menu_customization.themes.preset_names.pastel", {
        primaryColor: "#D97706",
        secondaryColor: "#FEF3C7",
        accentColor: "#F59E0B",
        backgroundColor: "#FFFBEB",
        surfaceColor: "#FFFFFF",
        textColor: "#451A03",
        buttonColor: "#D97706",
    }),
    createPreset("brigadeiro", "public_menu_customization.themes.preset_names.brigadeiro", {
        primaryColor: "#78350F",
        secondaryColor: "#FDE68A",
        accentColor: "#92400E",
        backgroundColor: "#FFFBEB",
        surfaceColor: "#FFFFFF",
        textColor: "#451A03",
        buttonColor: "#78350F",
        useGradientHeader: true,
    }),
    createPreset("sorvete", "public_menu_customization.themes.preset_names.sorvete", {
        primaryColor: "#EC4899",
        secondaryColor: "#FCE7F3",
        accentColor: "#38BDF8",
        backgroundColor: "#FDF4FF",
        surfaceColor: "#FFFFFF",
        textColor: "#831843",
        buttonColor: "#EC4899",
    }),
    createPreset("tapioca", "public_menu_customization.themes.preset_names.tapioca", {
        primaryColor: "#CA8A04",
        secondaryColor: "#FEF9C3",
        accentColor: "#A16207",
        backgroundColor: "#FEFCE8",
        surfaceColor: "#FFFFFF",
        textColor: "#422006",
        buttonColor: "#CA8A04",
        cardLayout: "compact-right",
    }),
    createPreset("coxinha", "public_menu_customization.themes.preset_names.coxinha", {
        primaryColor: "#EA580C",
        secondaryColor: "#FFEDD5",
        accentColor: "#B45309",
        backgroundColor: "#FFF7ED",
        surfaceColor: "#FFFFFF",
        textColor: "#7C2D12",
        buttonColor: "#EA580C",
    }),
    createPreset("feijoada", "public_menu_customization.themes.preset_names.feijoada", {
        primaryColor: "#292524",
        secondaryColor: "#E7E5E4",
        accentColor: "#16A34A",
        backgroundColor: "#FAFAF9",
        surfaceColor: "#FFFFFF",
        textColor: "#1C1917",
        buttonColor: "#292524",
        cardLayout: "compact-left",
    }),
    createPreset("moqueca", "public_menu_customization.themes.preset_names.moqueca", {
        primaryColor: "#F97316",
        secondaryColor: "#FED7AA",
        accentColor: "#0891B2",
        backgroundColor: "#FFF7ED",
        surfaceColor: "#FFFFFF",
        textColor: "#7C2D12",
        buttonColor: "#0891B2",
        useGradientHeader: true,
    }),
    createPreset("hamburguer-artesanal", "public_menu_customization.themes.preset_names.hamburguer_artesanal", {
        primaryColor: "#B45309",
        secondaryColor: "#27272A",
        accentColor: "#EF4444",
        backgroundColor: "#18181B",
        surfaceColor: "#27272A",
        textColor: "#FAFAFA",
        buttonColor: "#EF4444",
        useGradientHeader: true,
    }),
    createPreset("hot-dog", "public_menu_customization.themes.preset_names.hot_dog", {
        primaryColor: "#DC2626",
        secondaryColor: "#FEF3C7",
        accentColor: "#FACC15",
        backgroundColor: "#FFF7ED",
        surfaceColor: "#FFFFFF",
        textColor: "#450A0A",
        buttonColor: "#DC2626",
    }),
    createPreset("mexicano", "public_menu_customization.themes.preset_names.mexicano", {
        primaryColor: "#15803D",
        secondaryColor: "#DCFCE7",
        accentColor: "#DC2626",
        backgroundColor: "#F0FDF4",
        surfaceColor: "#FFFFFF",
        textColor: "#14532D",
        buttonColor: "#DC2626",
        useGradientHeader: true,
    }),
    createPreset("japones", "public_menu_customization.themes.preset_names.japones", {
        primaryColor: "#BE123C",
        secondaryColor: "#FCE7F3",
        accentColor: "#111827",
        backgroundColor: "#FFF1F2",
        surfaceColor: "#FFFFFF",
        textColor: "#4C0519",
        buttonColor: "#BE123C",
        cardLayout: "compact-right",
    }),
    createPreset("italiano", "public_menu_customization.themes.preset_names.italiano", {
        primaryColor: "#DC2626",
        secondaryColor: "#DCFCE7",
        accentColor: "#16A34A",
        backgroundColor: "#F8FAFC",
        surfaceColor: "#FFFFFF",
        textColor: "#14532D",
        buttonColor: "#16A34A",
        useGradientHeader: true,
    }),
    createPreset("arabe", "public_menu_customization.themes.preset_names.arabe", {
        primaryColor: "#047857",
        secondaryColor: "#D1FAE5",
        accentColor: "#B45309",
        backgroundColor: "#ECFDF5",
        surfaceColor: "#FFFFFF",
        textColor: "#064E3B",
        buttonColor: "#047857",
    }),
    createPreset("frutos-do-mar", "public_menu_customization.themes.preset_names.frutos_do_mar", {
        primaryColor: "#0284C7",
        secondaryColor: "#E0F2FE",
        accentColor: "#0D9488",
        backgroundColor: "#F0F9FF",
        surfaceColor: "#FFFFFF",
        textColor: "#0C4A6E",
        buttonColor: "#0284C7",
        useGradientHeader: true,
    }),
    createPreset("doceria", "public_menu_customization.themes.preset_names.doceria", {
        primaryColor: "#DB2777",
        secondaryColor: "#FCE7F3",
        accentColor: "#8B5CF6",
        backgroundColor: "#FDF4FF",
        surfaceColor: "#FFFFFF",
        textColor: "#581C87",
        buttonColor: "#DB2777",
        useGradientHeader: true,
    }),
    createPreset("padaria", "public_menu_customization.themes.preset_names.padaria", {
        primaryColor: "#B45309",
        secondaryColor: "#FEF3C7",
        accentColor: "#92400E",
        backgroundColor: "#FFFBEB",
        surfaceColor: "#FFFFFF",
        textColor: "#451A03",
        buttonColor: "#B45309",
        cardLayout: "compact-left",
    }),
    createPreset("massas", "public_menu_customization.themes.preset_names.massas", {
        primaryColor: "#CA8A04",
        secondaryColor: "#FEF9C3",
        accentColor: "#DC2626",
        backgroundColor: "#FEFCE8",
        surfaceColor: "#FFFFFF",
        textColor: "#422006",
        buttonColor: "#DC2626",
    }),
    createPreset("salada", "public_menu_customization.themes.preset_names.salada", {
        primaryColor: "#16A34A",
        secondaryColor: "#DCFCE7",
        accentColor: "#84CC16",
        backgroundColor: "#F0FDF4",
        surfaceColor: "#FFFFFF",
        textColor: "#14532D",
        buttonColor: "#16A34A",
    }),
    createPreset("poke", "public_menu_customization.themes.preset_names.poke", {
        primaryColor: "#0D9488",
        secondaryColor: "#CCFBF1",
        accentColor: "#F97316",
        backgroundColor: "#F0FDFA",
        surfaceColor: "#FFFFFF",
        textColor: "#134E4A",
        buttonColor: "#0D9488",
        useGradientHeader: true,
    }),
    createPreset("vinho-e-queijos", "public_menu_customization.themes.preset_names.vinho_e_queijos", {
        primaryColor: "#7F1D1D",
        secondaryColor: "#FEE2E2",
        accentColor: "#D97706",
        backgroundColor: "#FFF7ED",
        surfaceColor: "#FFFFFF",
        textColor: "#450A0A",
        buttonColor: "#7F1D1D",
        cardLayout: "compact-right",
    }),
    createPreset("milkshake", "public_menu_customization.themes.preset_names.milkshake", {
        primaryColor: "#C026D3",
        secondaryColor: "#FAE8FF",
        accentColor: "#F472B6",
        backgroundColor: "#FDF4FF",
        surfaceColor: "#FFFFFF",
        textColor: "#701A75",
        buttonColor: "#C026D3",
        useGradientHeader: true,
    }),
    createPreset("batata-frita", "public_menu_customization.themes.preset_names.batata_frita", {
        primaryColor: "#EAB308",
        secondaryColor: "#FEF9C3",
        accentColor: "#DC2626",
        backgroundColor: "#FEFCE8",
        surfaceColor: "#FFFFFF",
        textColor: "#422006",
        buttonColor: "#DC2626",
    }),
    createPreset("comida-mineira", "public_menu_customization.themes.preset_names.comida_mineira", {
        primaryColor: "#854D0E",
        secondaryColor: "#FEF3C7",
        accentColor: "#166534",
        backgroundColor: "#FFFBEB",
        surfaceColor: "#FFFFFF",
        textColor: "#451A03",
        buttonColor: "#854D0E",
        cardLayout: "compact-left",
    }),
]
